/**
 * ZAXIA Staff - Requests Page
 * 4-column kanban board for guest-reported maintenance faults
 *
 * Phase P2.9-2: Maintenance staff view with status updates
 *   reflected back to the guest My Room view via polling.
 *
 * Columns: Reported (NEW + ESCALATED) | Assigned | In Progress | Resolved
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  fetchGuestRequests,
  updateGuestRequestStatus,
  deleteGuestRequest,
  type ApiGuestRequest,
  GuestRequestStatus,
} from '../../api';

// ─── Constants ───────────────────────────────────────────────────────────────

const POLL_MS = 20_000;

const FAULT_PATTERNS: { pattern: RegExp; label: string; pillClass: string }[] = [
  { pattern: /ac|air.?con|heat/i,           label: 'AC / Heating', pillClass: 'bg-blue-900 text-blue-300 border-blue-700' },
  { pattern: /hot.?water/i,                 label: 'Hot Water',    pillClass: 'bg-orange-900 text-orange-300 border-orange-700' },
  { pattern: /shower|toilet|flush|plumb/i,  label: 'Plumbing',     pillClass: 'bg-teal-900 text-teal-300 border-teal-700' },
  { pattern: /light|lamp|bulb/i,            label: 'Lighting',     pillClass: 'bg-yellow-900 text-yellow-300 border-yellow-700' },
  { pattern: /\btv\b|television/i,          label: 'TV',           pillClass: 'bg-purple-900 text-purple-300 border-purple-700' },
  { pattern: /wi.?fi|internet|signal/i,     label: 'Wi-Fi',        pillClass: 'bg-cyan-900 text-cyan-300 border-cyan-700' },
  { pattern: /door|lock|jammed/i,           label: 'Door / Lock',  pillClass: 'bg-slate-700 text-slate-300 border-slate-500' },
  { pattern: /furniture|chair|bed|desk/i,   label: 'Furniture',    pillClass: 'bg-amber-900 text-amber-300 border-amber-700' },
];

const FILTER_LABELS = ['All', 'AC / Heating', 'Hot Water', 'Plumbing', 'Lighting', 'TV', 'Wi-Fi', 'Door / Lock', 'Furniture', 'Other'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getFaultMeta(description: string, category?: string): { label: string; pillClass: string } {
  for (const { pattern, label, pillClass } of FAULT_PATTERNS) {
    if (pattern.test(description)) return { label, pillClass };
  }
  // Fall back to the structured category field when description doesn't match a pattern
  if (category) {
    const cat = category.charAt(0) + category.slice(1).toLowerCase();
    return { label: cat, pillClass: 'bg-slate-700 text-slate-300 border-slate-500' };
  }
  return { label: 'Other', pillClass: 'bg-slate-700 text-slate-300 border-slate-500' };
}

function getFloor(roomNumber: string): string {
  if (roomNumber.length > 2) return roomNumber.slice(0, -2);
  return '1';
}

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

// ─── Column config ────────────────────────────────────────────────────────────

interface ColumnConfig {
  status: GuestRequestStatus;
  title: string;
  emptyText: string;
  headerClass: string;
  badgeClass: string;
}

const COLUMNS: ColumnConfig[] = [
  {
    // Also renders ESCALATED requests (higher urgency variant of NEW)
    status: GuestRequestStatus.NEW,
    title: 'Reported',
    emptyText: 'No new reports',
    headerClass: 'border-rose-600',
    badgeClass: 'bg-rose-600 text-white',
  },
  {
    status: GuestRequestStatus.ASSIGNED,
    title: 'Assigned',
    emptyText: 'No assigned requests',
    headerClass: 'border-violet-500',
    badgeClass: 'bg-violet-600 text-white',
  },
  {
    status: GuestRequestStatus.IN_PROGRESS,
    title: 'In Progress',
    emptyText: 'Nothing in progress',
    headerClass: 'border-amber-500',
    badgeClass: 'bg-amber-500 text-black',
  },
  {
    status: GuestRequestStatus.COMPLETED,
    title: 'Resolved',
    emptyText: 'No resolved items',
    headerClass: 'border-emerald-600',
    badgeClass: 'bg-emerald-600 text-white',
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

interface RequestCardProps {
  request: ApiGuestRequest;
  onClaim: (id: string) => void;
  onResolve: (id: string) => void;
  onReopen: (id: string) => void;
  onDelete: (id: string) => void;
  busy: boolean;
}

function RequestCard({ request, onClaim, onResolve, onReopen, onDelete, busy }: RequestCardProps) {
  const [confirmResolve, setConfirmResolve] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { label, pillClass } = getFaultMeta(request.description, request.category ?? undefined);
  const floor = getFloor(request.roomNumber);

  const handleResolveClick = () => {
    if (confirmResolve) {
      onResolve(request.id);
    } else {
      setConfirmResolve(true);
      setConfirmDelete(false);
    }
  };

  const handleDeleteClick = () => {
    if (confirmDelete) {
      onDelete(request.id);
    } else {
      setConfirmDelete(true);
      setConfirmResolve(false);
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 space-y-3">
      {/* Escalated badge */}
      {request.status === GuestRequestStatus.ESCALATED && (
        <div className="flex items-center gap-1.5 bg-rose-950/60 border border-rose-700/60 rounded px-2 py-1">
          <svg className="w-3 h-3 text-rose-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
          <span className="text-xs font-semibold text-rose-300 uppercase tracking-wide">Escalated</span>
        </div>
      )}

      {/* Header row — click to expand/collapse */}
      <button
        type="button"
        onClick={() => setIsExpanded(e => !e)}
        className="w-full text-left"
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-white font-bold text-lg leading-none">Room {request.roomNumber}</span>
              <span className="text-xs text-slate-400 bg-slate-700 px-1.5 py-0.5 rounded">Fl. {floor}</span>
            </div>
            <p className="text-slate-400 text-sm mt-0.5">{request.guestName}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded border ${pillClass}`}>{label}</span>
            <svg
              className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {/* Description always visible as a compact preview */}
        <p className={`text-slate-400 text-xs mt-1.5 truncate ${isExpanded ? 'hidden' : ''}`}>
          {request.description}
        </p>
      </button>

      {/* Expanded: full description + Timeline link + timestamps */}
      {isExpanded && (
        <>
          <p className="text-slate-300 text-sm leading-relaxed">{request.description}</p>

          <div className="flex justify-end">
            <Link
              to={`/requests/${request.id}`}
              className="text-[10px] text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30 px-2 py-1 rounded transition-colors font-medium"
              onClick={e => e.stopPropagation()}
            >
              Timeline →
            </Link>
          </div>

          {/* Timestamps */}
          <div className="space-y-1 pt-1 border-t border-slate-700">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="w-16 shrink-0 text-slate-600">Reported</span>
              <span>{formatDateTime(request.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="w-16 shrink-0 text-slate-600">Claimed</span>
              <span className={request.claimedAt ? 'text-amber-400' : ''}>
                {request.claimedAt ? formatDateTime(request.claimedAt) : '—'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="w-16 shrink-0 text-slate-600">Resolved</span>
              <span className={request.resolvedAt ? 'text-emerald-400' : ''}>
                {request.resolvedAt ? formatDateTime(request.resolvedAt) : '—'}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Confirm banners */}
      {confirmResolve && (
        <div className="bg-emerald-900/40 border border-emerald-700 rounded p-2 flex items-center justify-between gap-2">
          <span className="text-emerald-300 text-xs">Mark as resolved?</span>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmResolve(false)}
              className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleResolveClick}
              disabled={busy}
              className="text-xs bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-3 py-1 rounded transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="bg-rose-900/40 border border-rose-700 rounded p-2 flex items-center justify-between gap-2">
          <span className="text-rose-300 text-xs">Delete this report?</span>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteClick}
              disabled={busy}
              className="text-xs bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white px-3 py-1 rounded transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!confirmResolve && !confirmDelete && (
        <div className="flex gap-2 pt-1">
          {(request.status === GuestRequestStatus.NEW || request.status === GuestRequestStatus.ESCALATED) && (
            <button
              onClick={() => onClaim(request.id)}
              disabled={busy}
              className="flex-1 text-xs bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white py-1.5 rounded transition-colors font-medium"
            >
              Claim
            </button>
          )}
          {request.status === GuestRequestStatus.ASSIGNED && (
            <button
              onClick={() => onClaim(request.id)}
              disabled={busy}
              className="flex-1 text-xs bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white py-1.5 rounded transition-colors font-medium"
            >
              Start
            </button>
          )}
          {request.status === GuestRequestStatus.IN_PROGRESS && (
            <button
              onClick={handleResolveClick}
              disabled={busy}
              className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white py-1.5 rounded transition-colors font-medium"
            >
              Resolve
            </button>
          )}
          {request.status === GuestRequestStatus.COMPLETED && (
            <button
              onClick={() => onReopen(request.id)}
              disabled={busy}
              className="flex-1 text-xs bg-slate-600 hover:bg-slate-500 disabled:opacity-50 text-white py-1.5 rounded transition-colors font-medium"
            >
              Reopen
            </button>
          )}
          {/* Delete only available for non-resolved requests — preserves guest history */}
          {request.status !== GuestRequestStatus.COMPLETED && (
            <button
              onClick={handleDeleteClick}
              disabled={busy}
              className="text-xs text-rose-400 hover:text-rose-300 hover:bg-rose-900/30 disabled:opacity-50 px-3 py-1.5 rounded transition-colors"
              title="Delete report"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function RequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const slaBreachFilter = searchParams.get('filter') === 'sla-breached';

  const [requests, setRequests] = useState<ApiGuestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [activeFilter, setActiveFilter] = useState('All');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Fetch ────────────────────────────────────────────────────────────────

  const load = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const all = await fetchGuestRequests();
      // When arriving from the SLA breach panel, show all open requests across every
      // category so the supervisor can see exactly what is breaching SLA.
      const subset = slaBreachFilter
        ? all.filter((r) => r.status !== GuestRequestStatus.COMPLETED)
        : all;
      // Sort oldest-first so the worst offenders are immediately visible
      subset.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      setRequests(subset);
      setError(null);
    } catch {
      setError('Failed to load requests.');
    } finally {
      setLoading(false);
    }
  }, [slaBreachFilter]);

  useEffect(() => {
    load(true);
    timerRef.current = setInterval(() => load(false), POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [load]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const withBusy = async (id: string, fn: () => Promise<unknown>) => {
    setBusyIds((prev) => new Set(prev).add(id));
    try {
      await fn();
      await load(false);
    } catch {
      setError('Action failed. Please try again.');
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleClaim = (id: string) =>
    withBusy(id, () => updateGuestRequestStatus(id, GuestRequestStatus.IN_PROGRESS));

  const handleResolve = (id: string) =>
    withBusy(id, () => updateGuestRequestStatus(id, GuestRequestStatus.COMPLETED));

  const handleReopen = (id: string) =>
    withBusy(id, () => updateGuestRequestStatus(id, GuestRequestStatus.IN_PROGRESS));

  const handleDelete = (id: string) =>
    withBusy(id, () => deleteGuestRequest(id));

  // ── Filter ───────────────────────────────────────────────────────────────

  const filtered = activeFilter === 'All'
    ? requests
    : requests.filter((r) => getFaultMeta(r.description).label === activeFilter);

  // ESCALATED is an attention-state variant of NEW — fold it into the Reported column
  // so escalated items stay visible (and more prominent) rather than disappearing.
  const byStatus = (status: GuestRequestStatus) =>
    status === GuestRequestStatus.NEW
      ? filtered.filter(
          (r) => r.status === GuestRequestStatus.NEW || r.status === GuestRequestStatus.ESCALATED,
        )
      : filtered.filter((r) => r.status === status);

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">{slaBreachFilter ? 'Loading SLA-breached requests…' : 'Loading guest requests…'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white">{slaBreachFilter ? 'SLA-Breached Requests' : 'Guest Requests'}</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {requests.filter((r) => r.status !== GuestRequestStatus.COMPLETED).length} open ·{' '}
            {requests.filter((r) => r.status === GuestRequestStatus.COMPLETED).length} resolved
          </p>
        </div>
        <button
          onClick={() => load(false)}
          className="text-sm text-slate-400 hover:text-white flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-rose-900/40 border border-rose-700 rounded-lg px-4 py-3 flex items-center justify-between">
          <p className="text-rose-300 text-sm">{error}</p>
          <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-300 ml-4">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* SLA breach filter banner */}
      {slaBreachFilter && (
        <div className="bg-rose-950/60 border border-rose-700/60 rounded-lg px-4 py-3 flex items-center gap-3">
          <svg className="w-4 h-4 text-rose-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <p className="text-rose-300 text-sm">
            Showing all open requests — sorted oldest first (from SLA breach alert)
          </p>
        </div>
      )}

      {/* Filter pills — hidden in SLA breach mode (all categories shown, fault labels don't apply) */}
      <div className={`flex flex-wrap gap-2 ${slaBreachFilter ? 'hidden' : ''}`}>
        {FILTER_LABELS.map((lbl) => {
          const count = lbl === 'All'
            ? requests.filter((r) => r.status !== GuestRequestStatus.COMPLETED).length
            : requests.filter(
                (r) =>
                  r.status !== GuestRequestStatus.COMPLETED &&
                  getFaultMeta(r.description).label === lbl,
              ).length;
          return (
            <button
              key={lbl}
              onClick={() => {
                setActiveFilter(lbl);
                if (slaBreachFilter) setSearchParams({});
              }}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                activeFilter === lbl
                  ? 'bg-emerald-600 border-emerald-500 text-white'
                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'
              }`}
            >
              {lbl}
              {count > 0 && (
                <span className={`ml-1.5 ${activeFilter === lbl ? 'text-emerald-200' : 'text-slate-500'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Kanban board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
        {COLUMNS.map((col) => {
          const cards = byStatus(col.status);
          return (
            <div key={col.status} className="space-y-3">
              {/* Column header */}
              <div className={`flex items-center justify-between pb-2 border-b-2 ${col.headerClass}`}>
                <h2 className="text-sm font-semibold text-slate-200">{col.title}</h2>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${col.badgeClass}`}>
                  {cards.length}
                </span>
              </div>

              {/* Cards */}
              {cards.length === 0 ? (
                <div className="text-center py-8 text-slate-600 text-sm">{col.emptyText}</div>
              ) : (
                cards.map((r) => (
                  <RequestCard
                    key={r.id}
                    request={r}
                    onClaim={handleClaim}
                    onResolve={handleResolve}
                    onReopen={handleReopen}
                    onDelete={handleDelete}
                    busy={busyIds.has(r.id)}
                  />
                ))
              )}
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {requests.length === 0 && !loading && (
        <div className="text-center py-16">
          <svg className="w-12 h-12 mx-auto mb-4 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-slate-500 text-sm">No maintenance requests</p>
        </div>
      )}
    </div>
  );
}
